// Data Room Types

export interface DataRoom {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'archived';
  entry_title: string | null;
  entry_content: string | null;
  require_email: boolean;
  require_nda: boolean;
  watermark_pdfs: boolean;
  allow_downloads: boolean;
  created_at: string;
  updated_at: string;
  folders?: DataRoomFolder[];
  // Computed fields returned by list query
  folder_count?: number;
  document_count?: number;
}

export interface DataRoomFolder {
  id: string;
  data_room_id: string;
  user_id: string;
  name: string;
  position: number;
  is_default: boolean;
  parent_folder_id?: string | null;
  created_at: string;
  updated_at: string;
  documents?: DataRoomDocument[];
}

export interface DataRoomDocument {
  id: string;
  folder_id: string;
  data_room_id: string;
  user_id: string;
  name: string;
  file_type: string;
  file_size: number;
  storage_path: string;
  original_filename: string;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface DataRoomShareLink {
  id: string;
  data_room_id: string;
  user_id: string;
  share_token: string;
  url_slug: string | null;
  expires_at: string | null;
  is_active: boolean;
  label: string | null;
  created_at: string;
}

export interface DataRoomVisitor {
  id: string;
  data_room_id: string;
  share_link_id: string | null;
  email: string | null;
  ip_address: string | null;
  user_agent: string | null;
  first_visit_at: string;
  last_visit_at: string;
  total_visits: number;
}

export interface DataRoomStats {
  total_views: number;
  unique_visitors: number;
  active_share_links: number;
  active_links: number;
  total_documents: number;
}

// Input Types
export interface CreateDataRoomInput {
  name: string;
  description?: string;
}

export interface UpdateDataRoomInput {
  name?: string;
  description?: string;
  status?: 'draft' | 'active' | 'archived';
  entry_title?: string;
  entry_content?: string;
  require_email?: boolean;
  require_nda?: boolean;
  watermark_pdfs?: boolean;
  allow_downloads?: boolean;
}

export interface CreateFolderInput {
  name: string;
  parent_folder_id?: string;
}

export interface CreateShareLinkInput {
  label?: string;
  expires_at?: string;
}

// Constants
export const DEFAULT_FOLDERS = [
  'Financials',
  'Legal & Corporate',
  'Product & Tech',
  'Team & HR',
];

export const SUPPORTED_FILE_TYPES: Record<string, { ext: string; label: string }> = {
  'application/pdf': { ext: 'pdf', label: 'PDF' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: 'docx', label: 'Word' },
  'application/msword': { ext: 'doc', label: 'Word (Legacy)' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { ext: 'xlsx', label: 'Excel' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { ext: 'pptx', label: 'PowerPoint' },
  'application/vnd.ms-powerpoint': { ext: 'ppt', label: 'PowerPoint (Legacy)' },
  'image/png': { ext: 'png', label: 'PNG' },
  'image/jpeg': { ext: 'jpg', label: 'JPEG' },
};

export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

// Helper to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// File-type metadata. `short` is the 3-character badge label (fits in a
// fixed-size tile, never overflows), `label` is the full human name shown
// in the modal header, `color` is the accent for the icon.
export interface FileTypeInfo {
  ext: string;
  short: string;
  label: string;
  color: string;
}

const TYPE_MAP: Record<string, FileTypeInfo> = {
  pdf:  { ext: 'pdf',  short: 'PDF', label: 'PDF',        color: '#EF4444' },
  docx: { ext: 'docx', short: 'DOC', label: 'Word',       color: '#3B82F6' },
  doc:  { ext: 'doc',  short: 'DOC', label: 'Word',       color: '#3B82F6' },
  xlsx: { ext: 'xlsx', short: 'XLS', label: 'Excel',      color: '#22C55E' },
  pptx: { ext: 'pptx', short: 'PPT', label: 'PowerPoint', color: '#F97316' },
  ppt:  { ext: 'ppt',  short: 'PPT', label: 'PowerPoint', color: '#F97316' },
  png:  { ext: 'png',  short: 'IMG', label: 'Image',      color: '#A855F7' },
  jpg:  { ext: 'jpg',  short: 'IMG', label: 'Image',      color: '#A855F7' },
  jpeg: { ext: 'jpg',  short: 'IMG', label: 'Image',      color: '#A855F7' },
};

// Resolves the canonical file_type for a row whose stored value might be
// 'unknown' (legacy uploads from before the MIME→ext mapping was fixed).
// Falls back to parsing the original filename so old rows render right
// without a re-upload.
export const resolveFileType = (
  fileType: string | null | undefined,
  originalFilename?: string | null
): string => {
  const t = (fileType || '').toLowerCase();
  if (t && t !== 'unknown' && TYPE_MAP[t]) return t;
  const fromName = (originalFilename || '').split('.').pop()?.toLowerCase() || '';
  return TYPE_MAP[fromName] ? fromName : t || 'unknown';
};

export const getFileTypeInfo = (fileType: string, fallbackFilename?: string | null): FileTypeInfo => {
  const resolved = resolveFileType(fileType, fallbackFilename);
  return (
    TYPE_MAP[resolved] || {
      ext: resolved || 'file',
      short: 'FILE',
      label: 'File',
      color: '#94A3B8',
    }
  );
};

// Slugify a name for use as the business segment in share URLs.
// Falls back to "untitled" when the result is empty (e.g. name is "!!!").
const slugifyName = (name: string): string => {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'untitled';
};

// Generate share URL from url_slug + room name, or fall back to legacy token URL.
// urlSlugOrToken: pass link.url_slug for new links, link.share_token for legacy links.
// roomName: required for new slug-based format; omit only when using legacy token format.
export const getShareUrl = (urlSlugOrToken: string, roomName?: string): string => {
  const baseUrl = typeof window !== 'undefined'
    ? window.location.origin
    : 'https://sendupdates.ai';

  if (roomName && urlSlugOrToken.startsWith('data-room-')) {
    return `${baseUrl}/data-room/${slugifyName(roomName)}/${urlSlugOrToken}`;
  }
  // Legacy fallback for old 32-char hex tokens
  return `${baseUrl}/room/${urlSlugOrToken}`;
};
